import React, { useState, useEffect } from "react";
import axios from "axios";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * NoteEditor handles BOTH creation (from invoice → /notes/from-invoice/:invoiceId?type=credit)
 * AND editing of existing notes (/notes/:id/edit). Items + party + GST are prefilled from the
 * source invoice when creating; editing keeps the existing data but re-computes totals.
 */
export default function NoteEditor() {
  const { id, invoiceId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const noteType = searchParams.get("type") || "credit";
  const isEdit = !!id;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState({
    note_type: noteType,
    ref_invoice_id: invoiceId || "",
    ref_invoice_no: "",
    customer_name: "",
    note_date: new Date().toISOString().split("T")[0],
    reason: "",
    gst_type: "cgst_sgst",
    notes: "",
    items: [],
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        if (isEdit) {
          const res = await axios.get(`${API}/notes/${id}`);
          if (!cancelled) {
            const n = res.data;
            setData({
              note_type: n.note_type,
              ref_invoice_id: n.ref_invoice_id,
              ref_invoice_no: n.ref_invoice_no,
              customer_name: n.customer_name,
              note_date: n.note_date,
              reason: n.reason,
              gst_type: n.gst_type,
              notes: n.notes || "",
              items: n.items || [],
            });
          }
        } else if (invoiceId) {
          const res = await axios.get(`${API}/invoices/${invoiceId}`);
          if (!cancelled) {
            const inv = res.data;
            setData(prev => ({
              ...prev,
              ref_invoice_no: inv.invoice_no,
              customer_name: inv.customer_name,
              gst_type: inv.gst_type || "cgst_sgst",
              // Prefill items from invoice so user can edit / delete what's being returned/added.
              // Unit is carried verbatim from source line so KG bills produce KG notes, etc.
              items: (inv.items || []).map(it => ({
                product_name: it.product_name,
                hsn_sac: it.hsn_sac || "",
                quantity: it.quantity,
                price: it.price,
                gst_rate: it.gst_rate || 0,
                unit: (it.unit || "PCS"),
              })),
            }));
          }
        }
      } catch (e) { console.error(e); alert("Could not load: " + (e.response?.data?.detail || e.message)); }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [id, invoiceId, isEdit]);

  const updateItem = (idx, key, val) => {
    setData(prev => {
      const items = [...prev.items];
      items[idx] = { ...items[idx], [key]: val };
      return { ...prev, items };
    });
  };
  const removeItem = (idx) => setData(prev => ({ ...prev, items: prev.items.filter((_, i) => i !== idx) }));
  const addItem = () => setData(prev => ({ ...prev, items: [...prev.items, { product_name: "", hsn_sac: "", quantity: 1, price: 0, gst_rate: 18, unit: "PCS" }] }));

  // Live totals preview
  const totals = data.items.reduce((acc, it) => {
    const qty = parseFloat(it.quantity) || 0;
    const price = parseFloat(it.price) || 0;
    const gst = parseFloat(it.gst_rate) || 0;
    const tx = qty * price;
    const t = tx * (1 + gst / 100);
    acc.subtotal += tx;
    acc.total += t;
    acc.tax += t - tx;
    return acc;
  }, { subtotal: 0, total: 0, tax: 0 });

  const save = async () => {
    if (!data.reason.trim()) { alert("Please enter a reason for this note."); return; }
    if (!data.items.length) { alert("At least one line item is required."); return; }
    setSaving(true);
    try {
      const payload = {
        note_type: data.note_type,
        ref_invoice_id: data.ref_invoice_id,
        note_date: data.note_date,
        reason: data.reason,
        gst_type: data.gst_type,
        notes: data.notes,
        items: data.items.map(it => ({
          product_name: it.product_name,
          hsn_sac: it.hsn_sac || "",
          quantity: parseFloat(it.quantity) || 0,
          price: parseFloat(it.price) || 0,
          gst_rate: parseFloat(it.gst_rate) || 0,
          amount: 0,
          unit: (it.unit || "PCS"),
        })),
      };
      if (isEdit) {
        await axios.put(`${API}/notes/${id}`, {
          note_date: payload.note_date,
          reason: payload.reason,
          gst_type: payload.gst_type,
          notes: payload.notes,
          items: payload.items,
        });
      } else {
        await axios.post(`${API}/notes`, payload);
      }
      navigate("/notes");
    } catch (e) { alert("Save failed: " + (e.response?.data?.detail || e.message)); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="p-6 text-slate-500">Loading…</div>;

  const isCredit = data.note_type === "credit";
  const accent = isCredit ? "bg-red-600 hover:bg-red-700" : "bg-emerald-600 hover:bg-emerald-700";
  const title = isCredit ? "Credit Note" : "Debit Note";

  return (
    <div className="p-6 max-w-5xl" data-testid="note-editor">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">{isEdit ? "Edit" : "New"} {title}</h1>
          <p className="text-xs text-slate-500">Ref invoice: <b>{data.ref_invoice_no || "(linked at save)"}</b> &middot; Party: <b>{data.customer_name || "—"}</b></p>
        </div>
        <Button type="button" onClick={() => navigate(-1)} variant="outline" data-testid="cancel-btn" className="text-xs">Cancel</Button>
      </div>

      <div className="bg-white border border-slate-200 rounded-sm p-6 space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <Label>Date</Label>
            <Input type="date" data-testid="note-date" value={data.note_date} onChange={(e) => setData({ ...data, note_date: e.target.value })} />
          </div>
          <div>
            <Label>GST Type</Label>
            <select
              data-testid="note-gst-type"
              value={data.gst_type}
              onChange={(e) => setData({ ...data, gst_type: e.target.value })}
              className="w-full border border-slate-300 rounded-sm px-3 py-2 text-sm bg-white"
            >
              <option value="cgst_sgst">CGST + SGST (intrastate)</option>
              <option value="igst">IGST (interstate)</option>
            </select>
          </div>
          <div>
            <Label>Note Type</Label>
            <select
              data-testid="note-type-select"
              value={data.note_type}
              onChange={(e) => setData({ ...data, note_type: e.target.value })}
              disabled={isEdit}
              className="w-full border border-slate-300 rounded-sm px-3 py-2 text-sm bg-white disabled:bg-slate-100"
            >
              <option value="credit">Credit Note (reduces GST liability)</option>
              <option value="debit">Debit Note (increases GST liability)</option>
            </select>
          </div>
        </div>

        <div>
          <Label>Reason / Description *</Label>
          <Textarea
            data-testid="note-reason"
            autoCap
            value={data.reason}
            onChange={(e) => setData({ ...data, reason: e.target.value })}
            placeholder="e.g. Goods returned (2 units defective), price revision, post-sale discount, undercharged on original invoice…"
            className="min-h-[80px]"
            required
          />
          <p className="text-xs text-slate-400 mt-1">Mandatory under GST law — printed on the PDF.</p>
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <Label className="mb-0">Line Items</Label>
            <Button type="button" onClick={addItem} data-testid="add-item-btn" size="sm" className="bg-slate-900 hover:bg-slate-800 text-white text-xs">+ Add Item</Button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm border border-slate-200">
              <thead className="bg-slate-50 text-xs uppercase">
                <tr>
                  <th className="px-2 py-2 text-left">Product</th>
                  <th className="px-2 py-2 text-left">HSN</th>
                  <th className="px-2 py-2 text-right">Qty</th>
                  <th className="px-2 py-2 text-left">Unit</th>
                  <th className="px-2 py-2 text-right">Rate</th>
                  <th className="px-2 py-2 text-right">GST %</th>
                  <th className="px-2 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((it, idx) => (
                  <tr key={`item-${idx}`} className="border-t border-slate-100">
                    <td className="px-2 py-1"><Input data-testid={`item-name-${idx}`} autoCap value={it.product_name} onChange={(e) => updateItem(idx, "product_name", e.target.value)} /></td>
                    <td className="px-2 py-1"><Input data-testid={`item-hsn-${idx}`} value={it.hsn_sac} onChange={(e) => updateItem(idx, "hsn_sac", e.target.value)} className="w-24" /></td>
                    <td className="px-2 py-1"><Input data-testid={`item-qty-${idx}`} type="number" value={it.quantity} onChange={(e) => updateItem(idx, "quantity", e.target.value)} className="w-20 text-right" /></td>
                    <td className="px-2 py-1"><Input data-testid={`item-unit-${idx}`} value={it.unit || "PCS"} onChange={(e) => updateItem(idx, "unit", e.target.value.toUpperCase())} className="w-20" title="Unit of measure (KG / PCS / LTR / MTR / …) — must match source invoice" /></td>
                    <td className="px-2 py-1"><Input data-testid={`item-price-${idx}`} type="number" value={it.price} onChange={(e) => updateItem(idx, "price", e.target.value)} className="w-28 text-right" /></td>
                    <td className="px-2 py-1"><Input data-testid={`item-gst-${idx}`} type="number" value={it.gst_rate} onChange={(e) => updateItem(idx, "gst_rate", e.target.value)} className="w-20 text-right" /></td>
                    <td className="px-2 py-1 text-right"><button type="button" data-testid={`remove-item-${idx}`} onClick={() => removeItem(idx)} className="text-xs text-red-600 font-semibold">Remove</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="flex justify-end gap-6 pt-2 border-t border-slate-100">
          <div className="text-right">
            <p className="text-xs text-slate-500">Subtotal: <b className="text-slate-900">{"\u20B9"}{totals.subtotal.toFixed(2)}</b></p>
            <p className="text-xs text-slate-500">GST: <b className="text-slate-900">{"\u20B9"}{totals.tax.toFixed(2)}</b></p>
            <p className="text-base text-slate-900 font-bold mt-1">Total: {"\u20B9"}{totals.total.toFixed(2)}</p>
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" onClick={save} disabled={saving} data-testid="save-note-btn" className={`text-white text-sm font-semibold ${accent}`}>
            {saving ? "Saving…" : (isEdit ? "Save changes" : `Create ${title}`)}
          </Button>
        </div>
      </div>
    </div>
  );
}
